import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { marketingApi } from '@/lib/api';

const STORAGE_KEY  = 'loom_opportunity_banner_hidden';   // user preference toggle
const DISMISS_KEY  = 'loom_opportunity_banner_dismissed'; // per-item dismiss

/** Read / write the user's preference (true = hidden). */
export const readOpportunityBannerPref  = () => localStorage.getItem(STORAGE_KEY) === '1';
export const writeOpportunityBannerPref = (hidden) => {
  localStorage.setItem(STORAGE_KEY, hidden ? '1' : '0');
  window.dispatchEvent(new CustomEvent('loom:opportunity-banner-pref'));
};

export const OpportunityBanner = ({ hidden = false, onOpen }) => {
  const [item, setItem] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1',
  );
  const [prefHidden, setPrefHidden] = useState(readOpportunityBannerPref);

  const load = useCallback(() => {
    marketingApi.opportunities({ days: 30 })
      .then((data) => {
        const next = (data.items || []).find((row) => row.high_interest) || null;
        // If a new item arrives that differs from what was dismissed, re-surface it.
        if (next && next.label !== localStorage.getItem(DISMISS_KEY + '_label')) {
          setDismissed(false);
        }
        setItem(next);
      })
      .catch(() => setItem(null));
  }, []);

  useEffect(() => {
    load();
    window.addEventListener('marketing:opportunities-updated', load);
    return () => window.removeEventListener('marketing:opportunities-updated', load);
  }, [load]);

  // Listen for preference changes from the Settings dialog.
  useEffect(() => {
    const onPref = () => setPrefHidden(readOpportunityBannerPref());
    window.addEventListener('loom:opportunity-banner-pref', onPref);
    return () => window.removeEventListener('loom:opportunity-banner-pref', onPref);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    if (item) localStorage.setItem(DISMISS_KEY + '_label', item.label);
    setDismissed(true);
  };

  const openMarketing = () => onOpen?.();

  if (hidden || dismissed || prefHidden || !item) return null;
  return (
    <div
      className="flex min-h-9 flex-wrap items-center gap-2 border-b border-orange-300/40 bg-orange-500/10 px-3 py-1.5 text-amber-950 backdrop-blur-sm dark:border-orange-700/30 dark:bg-orange-500/8 dark:text-amber-200"
      role="status"
      data-testid="workspace-opportunities-banner"
    >
      <Sparkles className="h-3.5 w-3.5 shrink-0 text-orange-500 dark:text-orange-400" />
      <strong className="text-[11px]">High reader interest:</strong>
      <span className="min-w-0 flex-1 text-[10.5px]" data-testid="workspace-opportunities-banner-message">
        {item.label} · {item.reason}
      </span>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onPointerDown={openMarketing}
        onClick={openMarketing}
        className="h-7 gap-1 px-2 text-[10px] hover:bg-orange-200/50 dark:hover:bg-orange-800/30"
        data-testid="workspace-opportunities-open-marketing"
      >
        Open Marketing <ArrowRight className="h-3 w-3" />
      </Button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss opportunity"
        className="p-1 text-orange-700/70 transition-colors hover:text-orange-900 dark:text-orange-400/70 dark:hover:text-orange-300"
        data-testid="workspace-opportunities-dismiss-button"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default OpportunityBanner;
